import dayjs from "dayjs";
import { FaTimes, FaUserMd } from "react-icons/fa";
import { GiScalpel } from "react-icons/gi";
import { TbClockHour4, TbChecklist } from "react-icons/tb";

const AVATAR_COLORS = [
  "#EF4444",
  "#F59E0B",
  "#6366F1",
  "#22C55E",
  "#8B5CF6",
  "#06B6D4",
  "#EC4899",
  "#14B8A6",
];

const getAvatarBg = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const STATUS_STYLE = {
  Scheduled: "bg-blue-50 text-blue-600 border border-blue-200",
  SCHEDULED: "bg-blue-50 text-blue-600 border border-blue-200",
  "In Progress": "bg-amber-50 text-amber-700 border border-amber-200",
  IN_PROGRESS: "bg-amber-50 text-amber-700 border border-amber-200",
  Completed: "bg-green-50 text-green-700 border border-green-200",
  COMPLETED: "bg-green-50 text-green-700 border border-green-200",
  Cancelled: "bg-red-50 text-red-600 border border-red-200",
  CANCELLED: "bg-red-50 text-red-600 border border-red-200",
};

export default function OtDetailModal({ patient, onClose }) {
  if (!patient) return null;

  const patientName =
    patient.name ||
    patient.patientName ||
    (patient.firstName ? `${patient.firstName} ${patient.lastName || ""}`.trim() : "") ||
    "Patient";
  const avatarLetter = (patientName.charAt(0) || "P").toUpperCase();
  const avatarBg = patient.avatarBg || getAvatarBg(patientName);
  const status = patient.status || "Scheduled";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs overflow-y-auto animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 via-white to-purple-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 text-[#8B5CF6] flex items-center justify-center shadow-xs">
              <GiScalpel className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 font-sans">
                Surgery & OT Booking Details
              </h2>
              <p className="text-xs text-gray-500 font-sans">
                Booking ID:{" "}
                <span className="font-semibold text-purple-700 font-mono">
                  {patient.id || patient.otBookingId || patient.bookingId || "N/A"}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            title="Close"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-5 text-sm font-sans">
          {/* Patient Card Banner */}
          <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50/80 border border-gray-100">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0 shadow-xs"
              style={{ backgroundColor: avatarBg }}
            >
              {avatarLetter}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-bold text-gray-900 truncate">
                  {patientName}
                </h3>
                <span
                  className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                    STATUS_STYLE[status] || "bg-gray-100 text-gray-600"
                  }`}
                >
                  {status}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Patient ID:{" "}
                <span className="font-semibold text-gray-700">
                  {patient.patientId || "-"}
                </span>{" "}
                | Mobile:{" "}
                <span className="font-semibold text-gray-700">
                  {patient.mobile || "-"}
                </span>
              </p>
              <p className="text-xs text-gray-500">
                Age / Gender:{" "}
                <span className="font-semibold text-gray-700">
                  {patient.ageGender || "-"}
                </span>
              </p>
            </div>
          </div>

          {/* Procedure & OT Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl border border-gray-100 bg-white shadow-2xs">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-1">
                <TbChecklist className="text-purple-600 text-sm" />
                <span>Procedure</span>
              </div>
              <p className="text-sm font-bold text-gray-800">
                {patient.procedure || "N/A"}
              </p>
              {patient.procedureCode && (
                <p className="text-[11px] text-gray-400 mt-0.5 font-mono">
                  Code: {patient.procedureCode}
                </p>
              )}
            </div>

            <div className="p-4 rounded-xl border border-gray-100 bg-white shadow-2xs">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-1">
                <GiScalpel className="text-purple-600 text-sm" />
                <span>OT Room</span>
              </div>
              <p className="text-sm font-bold text-gray-800">
                {patient.otRoom || "N/A"}
              </p>
              {patient.theatreType && (
                <p className="text-[11px] text-gray-400 mt-0.5">
                  Type: {patient.theatreType}
                </p>
              )}
            </div>

            <div className="p-4 rounded-xl border border-gray-100 bg-white shadow-2xs">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-1">
                <TbClockHour4 className="text-purple-600 text-sm" />
                <span>Surgery Date & Time</span>
              </div>
              <p className="text-sm font-bold text-gray-800 whitespace-pre-line">
                {patient.surgeryDateTime || "-"}
              </p>
            </div>

            <div className="p-4 rounded-xl border border-gray-100 bg-white shadow-2xs">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-1">
                <FaUserMd className="text-purple-600 text-sm" />
                <span>Surgeon</span>
              </div>
              <p className="text-sm font-bold text-gray-800">
                {patient.doctor || "N/A"}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {patient.doctorRole || "General Surgeon"}
              </p>
            </div>
          </div>

          {/* Additional Clinical Notes if available */}
          {(patient.notes || patient.remarks || patient.clinicalNotes) && (
            <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/50">
              <h4 className="text-xs font-semibold text-gray-500 mb-1">
                Clinical Notes / Instructions
              </h4>
              <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                {patient.notes || patient.remarks || patient.clinicalNotes}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
